import AddIcon from '@mui/icons-material/Add';
import { Button, Stack, Typography } from '@mui/material';

// Quick Actions shortcut bar (top of /admin/program).
//
// Each button:
//  1. Expands the relevant section (and sub-item, where applicable) so the
//     target panel mounts.
//  2. Opens that panel's "Add" modal.
//
// IMS holds modal state locally. We expand the section, wait for mount, then
// click the panel's own Add button via a stable data-testid. The settle delay
// gives MUI's accordion transition + the panel's first render time to finish.

const SETTLE_MS_SECTION = 300; // section expand transition + first render

const expandSection = (sectionId: string): boolean => {
  const el = document.getElementById(`admin-section-${sectionId}-summary`);
  if (!el) return false;
  if (el.getAttribute('aria-expanded') === 'false') {
    (el as HTMLElement).click();
  }
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return true;
};

const clickByTestId = (testId: string) => {
  const el = document.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
  el?.click();
};

const QuickActions = () => {
  const handleAddSchedule = () => {
    if (!expandSection('schedule')) return;
    // IMS uses local state — wait for mount, then click its Add button.
    setTimeout(() => clickByTestId('ims-add-schedule'), SETTLE_MS_SECTION);
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Shortcuts to common admin tasks.
      </Typography>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddSchedule}
          sx={quickActionSx}
        >
          Add Schedule
        </Button>
      </Stack>
    </Stack>
  );
};

// Tyto deep-blue primary action, matches the rest of the admin pages.
// minWidth keeps the Quick Actions buttons at a uniform width so the row
// reads as a balanced shortcut bar.
const quickActionSx = {
  backgroundColor: '#1F4D83',
  '&:hover': { backgroundColor: '#163C66' },
  color: '#FFFFFF',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: 'none',
  borderRadius: 2,
  px: 2,
  minWidth: 200,
} as const;

export default QuickActions;
